import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import { type HttpError, useNavigation } from '@refinedev/core'
import { Breadcrumb, Button, Space, Table } from 'antd'
import { useState } from 'react'

import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'
import { searchByNameOrId } from '@/utils/searchFilters'

import { userColumns } from './columns'
import { ChangeBalanceModal } from './components/ChangeBalanceModal'
import { SetGroupModal } from './components/SetGroupModal'
import { SetRoleModal } from './components/SetRoleModal'
import { UsersFilterForm } from './components/UsersFilterForm'
import { useUserActionMenu } from './hooks/useUserActionMenu'

type AdminUserDto = components['schemas']['AdminUserDto']

interface UserFilters {
  search?: string
  accountStatus?: string
  paymentType?: string
  roleName?: string
  groupName?: string
}

export default function UsersListPage() {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const { show } = useNavigation()
  const {
    activeModal,
    targetUserId,
    modalExtra,
    openModal,
    closeModal,
    handleAction,
    getMenuItems,
  } = useUserActionMenu()

  const { tableProps, searchFormProps, filters } = useTable<
    AdminUserDto,
    HttpError,
    UserFilters
  >({
    resource: 'users',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      ...searchByNameOrId(values.search, 'ids', {
        asArray: true,
        textField: 'email',
      }),
      {
        field: 'accountStatus',
        operator: 'eq',
        value: values.accountStatus || undefined,
      },
      {
        field: 'paymentType',
        operator: 'eq',
        value: values.paymentType || undefined,
      },
      {
        field: 'roleName',
        operator: 'eq',
        value: values.roleName || undefined,
      },
      {
        field: 'groupName',
        operator: 'eq',
        value: values.groupName || undefined,
      },
    ],
  })

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Users' }]} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Space>
          {selectedRowKeys.length > 0 && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.65)' }}>
                {selectedRowKeys.length} selected
              </span>
              <Button
                onClick={() =>
                  openModal('role', '', { userIds: selectedRowKeys })
                }
              >
                Set Role
              </Button>
              <Button
                onClick={() =>
                  openModal('group', '', { userIds: selectedRowKeys })
                }
              >
                Set Group
              </Button>
            </>
          )}
        </Space>
      </div>

      <UsersFilterForm searchFormProps={searchFormProps} filters={filters} />

      <Table
        {...tableProps}
        pagination={tablePaginationConfig(tableProps)}
        columns={userColumns(getMenuItems, handleAction)}
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
        onRow={(record) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement
            if (
              target.closest('.ant-dropdown-trigger') ||
              target.closest('.ant-dropdown') ||
              target.closest('.ant-checkbox-wrapper') ||
              target.closest('.ant-btn') ||
              target.closest('[role="menu"]') ||
              target.closest('[role="menuitem"]')
            )
              return
            show('users', record.id)
          },
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 800 }}
      />

      <ChangeBalanceModal
        open={activeModal === 'balance'}
        userId={targetUserId}
        userEmail={modalExtra?.email as string}
        onClose={closeModal}
      />

      <SetRoleModal
        open={activeModal === 'role'}
        userIds={
          (modalExtra?.userIds as string[]) ??
          (targetUserId ? [targetUserId] : [])
        }
        userEmail={modalExtra?.email as string}
        currentRole={modalExtra?.currentRole as string}
        onClose={() => {
          closeModal()
          setSelectedRowKeys([])
        }}
      />

      <SetGroupModal
        open={activeModal === 'group'}
        userIds={
          (modalExtra?.userIds as string[]) ??
          (targetUserId ? [targetUserId] : [])
        }
        userEmail={modalExtra?.email as string}
        currentGroup={modalExtra?.currentGroup as string}
        onClose={() => {
          closeModal()
          setSelectedRowKeys([])
        }}
      />
    </div>
  )
}
